import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Broadcast, FacebookLogo, InstagramLogo, Lightning, XLogo, YoutubeLogo } from "@phosphor-icons/react";

import { Button } from "@/components/ui/button";

const quickLinks = [
  { label: "Breaking News", to: "/breaking-news" },
  { label: "Sports", to: "/sports" },
  { label: "What’s New", to: "/whats-new" },
  { label: "Investigations", to: "/investigations" },
];

const iconMap = {
  Instagram: InstagramLogo,
  Facebook: FacebookLogo,
  YouTube: YoutubeLogo,
  X: XLogo,
};

const STATIC_BRAND_LOGO = "https://customer-assets.emergentagent.com/job_content-watch-7/artifacts/thy81wjj_747B42A6-4C25-4A17-A557-B9BBE91C99DB%20Background%20Removed.png";
const cleanTickerHeadline = (value = "") => value.replace(/\s*\[[^\]]+\]\s*/g, " ").replace(/\s+/g, " ").trim();

const HeaderBrand = () => (
  <div className="flex items-center gap-5 sm:gap-6" data-testid="brand-block">
    <div className="flex h-[4.75rem] w-[4.75rem] shrink-0 items-center justify-center overflow-hidden rounded-sm border border-[rgba(10,15,26,0.12)] bg-white p-2 shadow-[0_10px_30px_rgba(10,15,26,0.06)] sm:h-[5.25rem] sm:w-[5.25rem]" data-testid="brand-logo-mark">
      <img alt="Content Watch logo" className="h-full w-full object-contain" data-testid="brand-logo-image" src={STATIC_BRAND_LOGO} />
    </div>
    <div className="space-y-2">
      <Link className="block font-[Montserrat] text-xl font-black uppercase tracking-[0.08em] text-[#0A0A0A] transition-colors hover:text-[#1338BE] sm:text-[2rem]" to="/" data-testid="brand-home-link">
        CONTENT WATCH HQ
      </Link>
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#1338BE] sm:text-xs" data-testid="brand-tagline">your headquarters for news now</p>
    </div>
  </div>
);

const HeaderActions = ({ socialLinks = [] }) => (
  <div className="flex flex-wrap items-center gap-4" data-testid="header-action-group">
    <div className="flex flex-wrap items-center gap-3" data-testid="header-social-links">
      {socialLinks.map((link) => {
        const Icon = iconMap[link.label] || Broadcast;
        return (
          <a
            key={link.label}
            className="inline-flex h-11 w-11 items-center justify-center rounded-sm border border-[rgba(10,15,26,0.12)] bg-white text-slate-800 transition-[background-color,transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-[#1338BE] hover:bg-slate-50"
            data-testid={`header-social-link-${link.label.toLowerCase()}`}
            href={link.url}
            aria-label={link.label}
            rel="noreferrer"
            target="_blank"
          >
            <Icon size={16} weight="fill" />
          </a>
        );
      })}
    </div>
  </div>
);

const BreakingTicker = ({ tickerItems = [] }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const items = useMemo(() => tickerItems.filter((item) => item?.headline && item?.slug), [tickerItems]);

  useEffect(() => {
    if (activeIndex >= items.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, items.length]);

  useEffect(() => {
    if (items.length <= 1) {
      return undefined;
    }

    const tickerInterval = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % items.length);
    }, 4200);

    return () => window.clearInterval(tickerInterval);
  }, [items.length]);

  const activeItem = items[activeIndex];

  return (
    <div className="bg-[#D21404] text-white" data-testid="site-header-breaking-ticker-bar">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-2.5 sm:px-6 lg:px-8">
        <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em]" data-testid="site-header-breaking-ticker-label">
          <Lightning size={14} weight="fill" />
          Breaking News
        </span>
        {activeItem ? (
          <Link
            className="min-w-0 flex-1 truncate text-xs font-medium uppercase tracking-[0.16em] text-white/90 transition-[opacity,transform] duration-300 hover:text-white"
            data-testid={`site-header-breaking-ticker-item-${activeItem.slug}`}
            to={`/stories/${activeItem.slug}`}
          >
            {cleanTickerHeadline(activeItem.headline)}
            {activeItem.location ? ` · ${activeItem.location}` : ""}
          </Link>
        ) : (
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-white/90" data-testid="site-header-breaking-ticker-copy">Stay Informed. Stay Alert.</p>
        )}
      </div>
    </div>
  );
};

export const SiteHeader = ({ siteData }) => {
  return (
    <header className="sticky top-0 z-50 border-b border-[#D9DCE1] bg-white/95 backdrop-blur-xl" data-testid="site-header">
      <BreakingTicker tickerItems={siteData?.ticker_items || []} />
      <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-x-8 gap-y-5 px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
        <HeaderBrand />
        <HeaderActions socialLinks={siteData?.social_links || []} />
      </div>
      <div className="border-t border-[#D9DCE1] bg-white" data-testid="site-header-quick-links-row">
        <nav className="mx-auto flex w-full max-w-7xl flex-wrap items-center gap-x-7 gap-y-3 px-4 py-4 sm:px-6 lg:px-8" data-testid="site-header-main-nav">
          {quickLinks.map((item) => (
            <Link
              key={item.to}
              className="text-xs font-semibold uppercase tracking-[0.16em] text-[#2E3138] transition-[color,transform] duration-200 hover:-translate-y-0.5 hover:text-[#1338BE]"
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-link`}
              to={item.to}
            >
              {item.label}
            </Link>
          ))}
        </nav>
      </div>
    </header>
  );
};